import type {
  Character,
  DrawAndPlaceAction,
  HorrorRaceState,
  Placement,
  SelectCharactersAction,
} from "@cloudnest/horror-race-engine";
import {
  applyAdvanceRace,
  applyAdvanceRound,
  applyConfirmRecap,
  applyConfirmRoundRecap,
  applyDrawCoins,
  applyForfeitTurn as applyForfeitTurnAction,
  applyPlaceCoins,
  applyResolveRound,
  applySelectCharacters,
  applyShufflePool,
  initGame,
  validateDrawAndPlace,
  validateSelectCharacters,
} from "@cloudnest/horror-race-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type GameEvent,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";
import { resolveHorrorRaceGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApi(): GameApi {
  const nextMode = resolveHorrorRaceGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("horror-race", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

interface MultiplayerGameState {
  gameState: HorrorRaceState | null;
  roomId: string | null;
  myParticipantId: string | null;
  isHost: boolean;
  lastAppliedSeq: number;
  lastError: string | null;
  // T9: the turn-point at which the active player requested a draw. The host
  // only fires shuffle_and_draw when this matches the current turn point, so
  // drawing is player-initiated instead of auto-firing. NOT part of the engine
  // state (HorrorRaceState) — it is a transient client signal, set when a
  // `request_draw` event is observed. null when no draw has been requested for
  // the current turn point.
  drawRequestSig: string | null;
}

const initialState: MultiplayerGameState = {
  gameState: null,
  roomId: null,
  myParticipantId: null,
  isHost: false,
  lastAppliedSeq: 0,
  lastError: null,
  drawRequestSig: null,
};

/**
 * Identifies a single turn point — race / round / draw-pass / turn index. Used
 * both to tag a draw request and to gate the host's shuffle_and_draw, so a
 * stale request from a previous turn never triggers a draw on the next turn.
 */
function turnPointSig(gameState: HorrorRaceState): string {
  return `${gameState.raceNumber}:${gameState.round}:${gameState.drawPass ?? 1}:${gameState.currentTurnIndex}`;
}

let state: MultiplayerGameState = { ...initialState };

// Guards a double-submit of place_coins for the SAME turn point. Once the first
// place is accepted the server advances the turn, so a second submit (e.g. a
// slow re-click, or the player not seeing the turn move because a WS event was
// missed) is rejected with "Not your turn" (422). Keyed by turn point so the
// next turn is never blocked. Cleared on failure (retry) and on reset.
let lastPlaceSubmitSig: string | null = null;

// Serializes catch-up polls: skip a new fetch while one is in flight so a slow
// API response can't pile up overlapping (redundant) requests.
let catchUpInFlight = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function applyEvent(
  gameState: HorrorRaceState,
  eventType: string,
  payload: Record<string, unknown>,
): HorrorRaceState {
  switch (eventType) {
    case "init":
      return payload.state as unknown as HorrorRaceState;

    case "select_characters": {
      const action: SelectCharactersAction = {
        type: "SELECT_CHARACTERS",
        playerId: payload.playerId as string,
        characters: payload.characters as [Character, Character],
      };
      return applySelectCharacters(gameState, action);
    }

    case "shuffle_pool":
      return applyShufflePool(gameState, payload.seed as number);

    case "draw_coins":
      return applyDrawCoins(gameState);

    case "place_coins": {
      const action: DrawAndPlaceAction = {
        type: "DRAW_AND_PLACE",
        playerId: payload.playerId as string,
        placements: payload.placements as [Placement, Placement, Placement],
      };
      return applyPlaceCoins(gameState, action);
    }

    // 토큰 포기: skip your draw turn (the game continues). Named distinctly from
    // the generic "forfeit" (= resign the match), which the game-api server
    // special-cases to skip validation + complete the room — that quit semantic
    // must NOT apply to this in-game turn action.
    case "forfeit_turn":
      return applyForfeitTurnAction(gameState);

    // T9: the active player's "I choose to draw" signal. It carries no engine
    // mutation — the host observes it (see applyRemoteEvent/replayEvents) and
    // only then submits shuffle_pool/draw_coins. Identity here so the engine
    // state is unchanged (parity with the validator, which also returns the
    // state unchanged).
    case "request_draw":
      return gameState;

    case "resolve_round":
      return applyResolveRound(gameState);

    // T18: a player acknowledged the race-end recap.
    case "confirm_recap":
      return applyConfirmRecap(gameState, {
        type: "CONFIRM_RECAP",
        playerId: payload.playerId as string,
      });

    // T18: "score_race" now ADVANCES past the recap (the tally was computed on
    // RACE_SCORING entry, after the finishing round recap advances) —
    // host-submitted once all confirmed.
    case "score_race":
      return applyAdvanceRace(gameState);

    // T26: a player acknowledged the round-end recap (ROUND_RECAP).
    case "confirm_round_recap":
      return applyConfirmRoundRecap(gameState, {
        type: "CONFIRM_ROUND_RECAP",
        playerId: payload.playerId as string,
      });

    // T26: host advances past the round recap to the next round — submitted once
    // every player has confirmed (the resolution was applied in resolve_round).
    case "advance_round":
      return applyAdvanceRound(gameState);

    default:
      console.warn(`Unknown event type: ${eventType}`);
      return gameState;
  }
}

interface ReplayResult {
  readonly gameState: HorrorRaceState | null;
  readonly lastAppliedSeq: number;
  readonly drawRequestSig: string | null;
}

/**
 * Fold the persisted event log into the final game state. The single source of
 * truth for replay, shared by `replayEvents` (guest/observer/reconnect) and the
 * host reconnect path in `initializeAsHost` — both must reconstruct the SAME
 * state the live event path produces. The `init` event sets the base state;
 * every later event is applied via the engine reducer.
 *
 * `drawRequestSig` is tracked exactly as in the live `applyRemoteEvent` path: a
 * `request_draw` tags the turn point it was made at, and any turn-completing
 * event (draw_coins / place_coins / forfeit_turn) clears it, so the host's
 * draw gate behaves identically after a replay.
 */
function replayFromEvents(events: readonly GameEvent[]): ReplayResult {
  let gameState: HorrorRaceState | null = null;
  let lastAppliedSeq = 0;
  let drawRequestSig: string | null = null;

  for (const event of events) {
    const payload = event.payload as Record<string, unknown>;
    if (event.eventType === "init") {
      gameState = payload.state as unknown as HorrorRaceState;
      drawRequestSig = null;
    } else if (gameState) {
      if (event.eventType === "request_draw") {
        drawRequestSig = turnPointSig(gameState);
      } else if (
        event.eventType === "draw_coins" ||
        event.eventType === "place_coins" ||
        event.eventType === "forfeit_turn"
      ) {
        drawRequestSig = null;
      }
      gameState = applyEvent(gameState, event.eventType, payload);
    }
    lastAppliedSeq = event.sequenceNumber;
  }

  return { gameState, lastAppliedSeq, drawRequestSig };
}

export const multiplayerGameActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: string[],
    playerNames: string[],
    totalRaces?: number,
  ): Promise<void> => {
    const myId = getCurrentUserId() ?? playerIds[0];

    state = {
      ...initialState,
      roomId,
      myParticipantId: myId,
      isHost: true,
      lastError: null,
    };
    notify();

    try {
      const eventsResponse = await getGameApi().getGameEvents(roomId);
      const existingEvents = eventsResponse.success
        ? (eventsResponse.data ?? [])
        : [];

      if (existingEvents.length > 0) {
        const replayed = replayFromEvents(existingEvents);
        state = {
          ...state,
          gameState: replayed.gameState,
          lastAppliedSeq: replayed.lastAppliedSeq,
          drawRequestSig: replayed.drawRequestSig,
        };
        notify();
        return;
      }
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error
            ? error.message
            : "이벤트를 불러올 수 없습니다",
      };
      notify();
      return;
    }

    // No events yet — brand-new game: create the initial state and persist it.
    const gameState = initGame({
      config: {
        playerCount: playerIds.length,
        trackLength: 12,
        totalRaces: totalRaces ?? (playerIds.length <= 6 ? 3 : 2),
        coinsPerCharacter: 5,
      },
      playerIds,
      playerNames,
      hostPlayerId: myId,
    });

    state = {
      ...state,
      gameState,
      roomId,
      myParticipantId: myId,
      isHost: true,
      lastAppliedSeq: 0,
      lastError: null,
    };
    notify();

    try {
      const response = await getGameApi().submitEvent(roomId, "init", {
        state: gameState as unknown as Record<string, unknown>,
      });
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "게임 초기화에 실패했습니다",
      };
      notify();
    }
  },

  initializeAsGuest: (roomId: string): void => {
    state = {
      ...initialState,
      roomId,
      myParticipantId: getCurrentUserId(),
      isHost: false,
      lastError: null,
    };
    notify();
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    state = {
      ...initialState,
      roomId,
      myParticipantId: null,
      isHost: false,
    };
    notify();

    await multiplayerGameActions.replayEvents(roomId);
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data) return;

      const replay = replayFromEvents(response.data);
      state = {
        ...state,
        gameState: replay.gameState,
        lastAppliedSeq: replay.lastAppliedSeq,
        drawRequestSig: replay.drawRequestSig,
      };
      notify();
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error
            ? error.message
            : "이벤트를 불러올 수 없습니다",
      };
      notify();
    }
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    if (!state.gameState && eventType !== "init") return;

    const currentState = state.gameState;
    const nextState = currentState
      ? applyEvent(currentState, eventType, payload)
      : eventType === "init"
        ? (payload.state as unknown as HorrorRaceState)
        : null;

    // T9: track the active player's draw request so the host fires the draw
    // only after it. request_draw tags the turn point it was made at (read from
    // the state BEFORE the identity apply); draw_coins clears it. Any other
    // event leaves it — a turn-advancing event moves the turn point so the
    // stale sig no longer matches the gate.
    let drawRequestSig = state.drawRequestSig;
    if (eventType === "request_draw" && currentState) {
      drawRequestSig = turnPointSig(currentState);
    } else if (
      eventType === "draw_coins" ||
      eventType === "place_coins" ||
      eventType === "forfeit_turn"
    ) {
      drawRequestSig = null;
    }

    state = {
      ...state,
      gameState: nextState,
      lastAppliedSeq: seq,
      drawRequestSig,
    };
    notify();
  },

  submitSelectCharacters: async (
    characters: readonly [Character, Character],
  ): Promise<void> => {
    if (!state.gameState || !state.roomId || !state.myParticipantId) return;

    const action: SelectCharactersAction = {
      type: "SELECT_CHARACTERS",
      playerId: state.myParticipantId,
      characters,
    };

    const validation = validateSelectCharacters(state.gameState, action);
    if (!validation.valid) {
      state = { ...state, lastError: validation.error ?? "유효하지 않은 선택" };
      notify();
      return;
    }

    try {
      await getGameApi().submitEvent(state.roomId, "select_characters", {
        playerId: state.myParticipantId,
        characters,
      });
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "선택 전송에 실패했습니다",
      };
      notify();
    }
  },

  submitDrawAndPlace: async (
    placements: readonly [Placement, Placement, Placement],
  ): Promise<void> => {
    if (!state.gameState || !state.roomId || !state.myParticipantId) return;

    const action: DrawAndPlaceAction = {
      type: "DRAW_AND_PLACE",
      playerId: state.myParticipantId,
      placements,
    };

    const validation = validateDrawAndPlace(state.gameState, action);
    if (!validation.valid) {
      state = { ...state, lastError: validation.error ?? "유효하지 않은 배치" };
      notify();
      return;
    }

    // Block a duplicate submit for this exact turn point — the first already
    // advanced the server's turn, so a second would 422 "Not your turn".
    const roomId = state.roomId;
    const sig = turnPointSig(state.gameState);
    if (lastPlaceSubmitSig === sig) return;
    lastPlaceSubmitSig = sig;

    try {
      const response = await getGameApi().submitEvent(roomId, "place_coins", {
        playerId: state.myParticipantId,
        placements,
      });
      if (!response.success) {
        // The server rejected it (typically "Not your turn") — this client is
        // behind, so resync from the authoritative log instead of leaving the
        // player stuck re-submitting.
        lastPlaceSubmitSig = null;
        state = {
          ...state,
          lastError: response.error ?? "배치 전송에 실패했습니다",
        };
        notify();
        void multiplayerGameActions.catchUpEvents(roomId);
      }
    } catch (error) {
      lastPlaceSubmitSig = null;
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "배치 전송에 실패했습니다",
      };
      notify();
      void multiplayerGameActions.catchUpEvents(roomId);
    }
  },

  // Reliability net for missed WS events: pull any events past lastAppliedSeq
  // and apply the ones we haven't seen. A single dropped `game:event` broadcast
  // otherwise strands a client (it never learns the turn advanced) with no
  // recovery but a full WS reconnect. applyRemoteEvent dedups by sequence, so
  // overlap with the live WS path is safe.
  catchUpEvents: async (roomId: string): Promise<void> => {
    if (!state.gameState) return;
    // Nothing more to catch up once the game is over — stop polling the API.
    if (state.gameState.phase === "GAME_OVER") return;
    if (catchUpInFlight) return;
    catchUpInFlight = true;
    try {
      const response = await getGameApi().getGameEvents(
        roomId,
        state.lastAppliedSeq,
      );
      if (!response.success || !response.data) return;
      for (const event of response.data) {
        multiplayerGameActions.applyRemoteEvent(
          event.eventType,
          event.payload as Record<string, unknown>,
          event.sequenceNumber,
        );
      }
    } catch {
      // Transient — the next poll retries.
    } finally {
      catchUpInFlight = false;
    }
  },

  // T9: the active player chooses to draw. Submits a lightweight `request_draw`
  // signal (no engine mutation). The host observes it and only then submits
  // shuffle_pool/draw_coins — so the draw is player-initiated, not auto-fired.
  // The seeded-shuffle determinism is preserved because the host remains the
  // sole submitter of shuffle_pool/draw_coins.
  submitRequestDraw: async (): Promise<void> => {
    if (!state.gameState || !state.roomId || !state.myParticipantId) return;

    try {
      await getGameApi().submitEvent(state.roomId, "request_draw", {
        playerId: state.myParticipantId,
      });
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "뽑기 요청에 실패했습니다",
      };
      notify();
    }
  },

  submitForfeit: async (): Promise<void> => {
    if (!state.gameState || !state.roomId) return;

    try {
      await getGameApi().submitEvent(state.roomId, "forfeit_turn", {
        playerId: state.myParticipantId,
      });
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "포기 전송에 실패했습니다",
      };
      notify();
    }
  },

  // T18: acknowledge the race-end recap. The host advances to the next race
  // (score_race = applyAdvanceRace) only once every player has confirmed.
  submitConfirmRecap: async (): Promise<void> => {
    if (!state.gameState || !state.roomId || !state.myParticipantId) return;

    try {
      await getGameApi().submitEvent(state.roomId, "confirm_recap", {
        playerId: state.myParticipantId,
      });
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "확인 전송에 실패했습니다",
      };
      notify();
    }
  },

  // T26: acknowledge the round-end recap. The host advances to the next round
  // (advance_round = applyAdvanceRound) only once every player has confirmed.
  submitConfirmRoundRecap: async (): Promise<void> => {
    if (!state.gameState || !state.roomId || !state.myParticipantId) return;

    try {
      await getGameApi().submitEvent(state.roomId, "confirm_round_recap", {
        playerId: state.myParticipantId,
      });
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "확인 전송에 실패했습니다",
      };
      notify();
    }
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    lastPlaceSubmitSig = null;
    catchUpInFlight = false;
    gameApi = null;
    gameApiMode = null;
    state = { ...initialState };
    notify();
  },
};

interface HostAction {
  readonly type:
    | "shuffle_and_draw"
    | "resolve_round"
    | "score_race"
    | "advance_round";
  readonly seed?: number;
}

/**
 * Base seed for deterministic shuffles. When absent, shuffles use `Date.now()`
 * (normal play). When present, the host derives a reproducible per-draw seed
 * (see `nextShuffleSeed`) so E2E can predict the draw sequence. Only the host
 * generates seeds (getHostAction runs host-only) and submits them in the
 * shuffle_pool event, so a single host-side base seed makes the whole game
 * deterministic.
 *
 * Source: `window.__HR_TEST_SEED__` (set by E2E via page.addInitScript so it
 * survives in-app navigation), with a `?seed=N` query param as a manual fallback.
 */
function getTestSeedBase(): number | null {
  if (typeof window === "undefined") return null;
  const fromWindow = (window as { __HR_TEST_SEED__?: number }).__HR_TEST_SEED__;
  if (typeof fromWindow === "number" && Number.isFinite(fromWindow)) {
    return fromWindow;
  }
  const raw = new URLSearchParams(window.location.search).get("seed");
  if (raw === null) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Reproducible per-draw seed derived from the base seed + current game point. */
function nextShuffleSeed(gameState: HorrorRaceState): number {
  const base = getTestSeedBase();
  if (base === null) return Date.now();
  return (
    base +
    gameState.raceNumber * 1_000_000 +
    gameState.round * 10_000 +
    (gameState.drawPass ?? 1) * 1_000 +
    gameState.currentTurnIndex * 100
  );
}

/**
 * The host's next automatic action, or null. T9: shuffle_and_draw is no longer
 * auto-fired the moment a turn starts — it fires only after the active player
 * has requested a draw (`drawRequestSig` matches the current turn point). All
 * other phase transitions (resolve_round / score_race) are unchanged.
 */
export function getHostAction(
  gameState: HorrorRaceState,
  drawRequestSig: string | null,
): HostAction | null {
  if (
    gameState.phase === "ROUND_DRAW_AND_PLACE" &&
    gameState.drawnCoins.length === 0 &&
    gameState.coinPool.length > 0 &&
    drawRequestSig === turnPointSig(gameState)
  ) {
    return { type: "shuffle_and_draw", seed: nextShuffleSeed(gameState) };
  }

  if (gameState.phase === "ROUND_RESOLVE") {
    return { type: "resolve_round" };
  }

  // T18: the recap is held in RACE_SCORING until every player confirms; only
  // then does the host advance to the next race (or GAME_OVER).
  if (gameState.phase === "RACE_SCORING") {
    const allConfirmed = gameState.players.every((p) => p.hasConfirmedRecap);
    if (allConfirmed) {
      return { type: "score_race" };
    }
  }

  // T26: the round recap is held in ROUND_RECAP until every player confirms;
  // only then does the host advance to the next round or race scoring.
  if (gameState.phase === "ROUND_RECAP") {
    const allConfirmed = gameState.players.every(
      (p) => p.hasConfirmedRoundRecap,
    );
    if (allConfirmed) {
      return { type: "advance_round" };
    }
  }

  return null;
}

export async function submitHostAction(
  roomId: string,
  action: HostAction,
): Promise<void> {
  switch (action.type) {
    case "shuffle_and_draw": {
      const seed = action.seed ?? Date.now();
      await getGameApi().submitEvent(roomId, "shuffle_pool", { seed });
      await getGameApi().submitEvent(roomId, "draw_coins", {});
      break;
    }
    case "resolve_round":
      await getGameApi().submitEvent(roomId, "resolve_round", {
        resolverId: state.myParticipantId ?? "",
      });
      break;
    case "score_race":
      await getGameApi().submitEvent(roomId, "score_race", {});
      break;
    case "advance_round":
      await getGameApi().submitEvent(roomId, "advance_round", {
        resolverId: state.myParticipantId ?? "",
      });
      break;
  }
}

export const multiplayerGameStore = {
  getState: (): MultiplayerGameState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useMultiplayerGame(): MultiplayerGameState {
  return useSyncExternalStore(
    multiplayerGameStore.subscribe,
    multiplayerGameStore.getState,
  );
}

export function useMultiplayerGameActions(): typeof multiplayerGameActions {
  return multiplayerGameActions;
}
